import { useMemo, useState, type ReactNode } from "react";

import { Button, Input, Stack } from "@/components/ui";

import { Paged } from "./Paged";

export interface Facet<T> {
  readonly key: string;
  readonly label: string;
  readonly values: (row: T) => string[];
}

export interface FilterBarProps<T> {
  readonly rows: readonly T[];
  readonly text: (row: T) => string;
  readonly facets: readonly Facet<T>[];
  readonly pageSize?: number;
  readonly children: (page: T[]) => ReactNode;
}

const CAP = 12;

/** A filter built for thousands of rows: one pass for the index, one page ever rendered. */
export function FilterBar<T>({ rows, text, facets, pageSize, children }: FilterBarProps<T>) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [openFacet, setOpenFacet] = useState<string | null>(null);
  const [facetQuery, setFacetQuery] = useState("");

  const index = useMemo(() => {
    const haystacks: string[] = [];
    const values: Record<string, string[]>[] = [];
    const counts = new Map<string, Map<string, number>>();
    for (const facet of facets) counts.set(facet.key, new Map());

    for (const row of rows) {
      haystacks.push(text(row).toLowerCase());
      const per: Record<string, string[]> = {};
      for (const facet of facets) {
        const found = facet.values(row);
        per[facet.key] = found;
        const tally = counts.get(facet.key);
        if (tally === undefined) continue;
        for (const value of found) tally.set(value, (tally.get(value) ?? 0) + 1);
      }
      values.push(per);
    }

    const sorted = new Map<string, string[]>();
    for (const facet of facets) {
      const tally = counts.get(facet.key) ?? new Map<string, number>();
      sorted.set(
        facet.key,
        [...tally.keys()].sort((a, b) => (tally.get(b) ?? 0) - (tally.get(a) ?? 0)),
      );
    }
    return { haystacks, values, counts, sorted };
  }, [rows, facets, text]);

  const needle = query.trim().toLowerCase();

  const matched = useMemo(() => {
    const active = Object.entries(picked).filter(([, values]) => values.length > 0);
    const out: T[] = [];
    for (let position = 0; position < rows.length; position += 1) {
      const row = rows[position];
      if (row === undefined) continue;
      if (needle !== "" && !(index.haystacks[position] ?? "").includes(needle)) continue;
      const held = index.values[position] ?? {};
      let keep = true;
      for (const [key, wanted] of active) {
        const mine = held[key] ?? [];
        if (!wanted.some((value) => mine.includes(value))) {
          keep = false;
          break;
        }
      }
      if (keep) out.push(row);
    }
    return out;
  }, [rows, index, needle, picked]);

  const toggle = (key: string, value: string): void => {
    setPicked((held) => {
      const current = held[key] ?? [];
      return {
        ...held,
        [key]: current.includes(value)
          ? current.filter((one) => one !== value)
          : [...current, value],
      };
    });
  };

  const activeCount = facets.reduce((total, facet) => total + (picked[facet.key] ?? []).length, 0);

  return (
    <Stack gap={4}>
      <div className="cx-filterbar">
        <Input
          value={query}
          placeholder="Search"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        {facets.map((facet) => {
          const chosen = picked[facet.key] ?? [];
          const all = index.sorted.get(facet.key) ?? [];
          const inner = openFacet === facet.key ? facetQuery.trim().toLowerCase() : "";
          const shown = inner === "" ? all : all.filter((one) => one.toLowerCase().includes(inner));
          const tally = index.counts.get(facet.key);
          return (
            <div className="cx-facet" key={facet.key}>
              <button
                type="button"
                className={chosen.length > 0 ? "cx-facet-btn cx-facet-btn-on" : "cx-facet-btn"}
                aria-expanded={openFacet === facet.key}
                onClick={() => {
                  setOpenFacet(openFacet === facet.key ? null : facet.key);
                  setFacetQuery("");
                }}
              >
                {facet.label}
                {chosen.length > 0 ? <span className="cx-facet-n">{chosen.length}</span> : null}
              </button>
              {openFacet === facet.key ? (
                <div className="cx-facet-menu">
                  {all.length > CAP ? (
                    <input
                      className="cx-facet-find"
                      value={facetQuery}
                      placeholder={facet.label}
                      aria-label={facet.label}
                      onChange={(event) => {
                        setFacetQuery(event.target.value);
                      }}
                    />
                  ) : null}
                  {shown.slice(0, CAP).map((value) => (
                    <button
                      type="button"
                      key={value}
                      className={
                        chosen.includes(value) ? "cx-facet-item cx-facet-item-on" : "cx-facet-item"
                      }
                      onClick={() => {
                        toggle(facet.key, value);
                      }}
                    >
                      <span className="cx-facet-mark">{chosen.includes(value) ? "on" : ""}</span>
                      <span className="cx-facet-v">{value}</span>
                      <span className="cx-facet-c">{tally?.get(value) ?? 0}</span>
                    </button>
                  ))}
                  {shown.length > CAP ? (
                    <div className="cx-facet-more">{`${String(shown.length - CAP)} more`}</div>
                  ) : null}
                  {shown.length === 0 ? <div className="cx-facet-more">No matches</div> : null}
                </div>
              ) : null}
            </div>
          );
        })}
        {activeCount > 0 || needle !== "" ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setPicked({});
              setQuery("");
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {activeCount > 0 ? (
        <div className="cx-chips">
          {facets.map((facet) =>
            (picked[facet.key] ?? []).map((value) => (
              <button
                type="button"
                className="cx-chip cx-chip-x"
                key={facet.key + value}
                onClick={() => {
                  toggle(facet.key, value);
                }}
              >
                {`${facet.label}: ${value}`}
              </button>
            )),
          )}
        </div>
      ) : null}

      <Paged rows={matched} pageSize={pageSize ?? 9}>
        {(page) => children(page)}
      </Paged>
    </Stack>
  );
}
